import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Prospect, CSVUploadResult, CSVProspectData } from '../types/prospect';

// Normalize any phone number input to E.164 format.
// US/Canada numbers without a country code are assumed to be +1.
// Returns null if the number is invalid.
function normalizePhoneE164(input: string): string | null {
  const parsed = parsePhoneNumberFromString(input, 'US');
  if (!parsed || !parsed.isValid()) return null;
  return parsed.number; // E.164 e.g. +15551234567
}

export function useProspects() {
  const createProspect = (
    prospectData: Omit<Prospect, 'id' | 'createdAt' | 'updatedAt'>
  ): Prospect => {
    return {
      ...prospectData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  };

  const parseCSV = (csvContent: string): CSVUploadResult => {
    const lines = csvContent.split('\n').filter((line) => line.trim());
    if (lines.length === 0) {
      return { successful: [], failed: [], duplicates: [] };
    }

    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const nameIndex = headers.findIndex((h) => h.includes('name'));
    const phoneIndex = headers.findIndex((h) => h.includes('phone'));
    const companyIndex = headers.findIndex((h) => h.includes('company'));
    const emailIndex = headers.findIndex((h) => h.includes('email'));

    if (nameIndex === -1 || phoneIndex === -1 || companyIndex === -1) {
      return {
        successful: [],
        failed: [
          {
            row: 1,
            data: headers,
            error: 'Required columns missing: Name, Phone, Company',
          },
        ],
        duplicates: [],
      };
    }

    const successful: Prospect[] = [];
    const failed: { row: number; data: any; error: string }[] = [];
    const seenPhones = new Set<string>();
    const duplicates: Prospect[] = [];

    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i]
        .split(',')
        .map((cell) => cell.trim().replace(/"/g, ''));

      try {
        const name = cells[nameIndex]?.trim();
        const rawPhone = cells[phoneIndex]?.trim();
        const company = cells[companyIndex]?.trim();
        const email = emailIndex >= 0 ? cells[emailIndex]?.trim() : undefined;

        const errors: string[] = [];

        if (!name) errors.push('Missing name');
        if (!rawPhone) errors.push('Missing phone');
        if (!company) errors.push('Missing company');

        let phone: string | null = null;
        if (rawPhone) {
          phone = normalizePhoneE164(rawPhone);
          if (!phone) errors.push('Invalid phone number — US: enter 10 digits, international: include + and country code');
        }

        if (errors.length > 0) {
          failed.push({
            row: i + 1,
            data: cells,
            error: errors.join(', '),
          });
          continue;
        }

        if (seenPhones.has(phone!)) {
          duplicates.push(createProspect({ name: name!, phone: phone!, company: company!, email }));
          continue;
        }

        seenPhones.add(phone!);
        successful.push(createProspect({ name: name!, phone: phone!, company: company!, email }));
      } catch (error) {
        failed.push({
          row: i + 1,
          data: cells,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }

    return { successful, failed, duplicates };
  };

  // Validates that a phone number can be normalized to E.164.
  const validatePhone = (phone: string): boolean => {
    return normalizePhoneE164(phone) !== null;
  };

  // Returns the E.164-normalized form of a phone number.
  // Falls back to the original input if parsing fails (shouldn't happen after validatePhone passes).
  const formatPhone = (phone: string): string => {
    return normalizePhoneE164(phone) ?? phone;
  };

  return {
    createProspect,
    parseCSV,
    validatePhone,
    formatPhone,
  };
}
